import { useTranslation } from 'react-i18next'
import SEO from '@/utils/seo'
import {
  buildHomePageJsonLd,
  buildPersonForFounder,
} from '@/utils/structuredData'
import { cmsHomeSections } from '@/content/cms/sections.generated'
import { SECTION_REGISTRY, resolveHomeSections } from '@/content/sections'

/**
 * HomePage — the section list is CMS-driven.
 *
 * Order and visibility come from `cmsHomeSections` (generated from
 * /api/cms/public/home-sections); `src/content/sections.ts` maps each key to
 * the component that renders it. This used to be a fixed JSX sequence, which
 * meant reordering or hiding a section was a code change and a deploy.
 *
 * Two safeguards, because the empty case is the dangerous one:
 *
 *  - If the generated file has no rows (an operator emptied the CMS, or the
 *    build ran with SKIP_CMS_SYNC against a stale file) we fall back to the
 *    registry's declaration order with everything visible. That is exactly the
 *    page as it shipped, so the homepage can never come up empty.
 *  - Keys with no registered component are skipped by resolveHomeSections with
 *    a console warning, so a CMS row added ahead of a deploy degrades to "one
 *    section missing" rather than a blank page.
 *
 * The registry also owns the hidden-but-wired sections (testimonials, team);
 * re-enabling either is now a toggle in the admin, not a code edit.
 */
export default function HomePage() {
  const { t } = useTranslation()

  const sections =
    cmsHomeSections.length > 0
      ? resolveHomeSections(cmsHomeSections)
      : Object.values(SECTION_REGISTRY)
          // Same registry-level `enabled` filter as resolveHomeSections.
          // Without this the empty-CMS fallback would re-include any
          // code-disabled section (e.g. AboutSnippet) and ship it to
          // visitors even though the live (CMS-driven) homepage is
          // hiding it.
          .filter((def) => def.enabled !== false)
          .map((def) => ({ key: def.key, def, settings: def.defaults }))

  return (
    <>
      <SEO
        title={t('seo.homeTitle')}
        description={t('seo.homeDescription')}
        keywords={t('seo.homeKeywords')}
        jsonLd={[
          ...buildHomePageJsonLd(),
          // Founder Person schema so Google Knowledge Panel can link the org
          // to a person entity. Per-locale description / name are sourced
          // from i18n so the JSON-LD description matches the visible copy.
          buildPersonForFounder({
            description: t('seo.founderDescription'),
            jobTitle: t('seo.founderJobTitle'),
          }),
        ]}
      />
      {sections.map(({ key, def, settings }) => (
        <def.Component key={key} settings={settings} />
      ))}
    </>
  )
}